/**
 * Side-by-side diff of two checkpoint DesignSpecs. Opens via the "compare"
 * button on a CheckpointCard. Shows what was added, removed, and changed
 * across parts, joints, features, parameters, and the OpenSCAD code hash.
 *
 * No external diff library — produces a concise, user-readable summary using
 * shallow set comparisons keyed by id.
 */
import { useMemo } from 'react';
import { X, Plus, Minus, Pencil, GitCompare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { Checkpoint } from '@/services/checkpointService';
import type { DesignSpec } from '@shared/spec';

type Props = {
  base: Checkpoint;
  head: Checkpoint;
  baseLabel: string;
  headLabel: string;
  onClose: () => void;
};

type IdName = { id: string; label: string };

function indexById<T extends { id: string }>(items: T[] | undefined, label: (t: T) => string) {
  const map = new Map<string, IdName & { item: T }>();
  for (const item of items ?? []) {
    map.set(item.id, { id: item.id, label: label(item), item });
  }
  return map;
}

function diffByKey<T extends { id: string }>(
  left: T[] | undefined,
  right: T[] | undefined,
  label: (t: T) => string,
  changed: (a: T, b: T) => boolean,
) {
  const lhs = indexById(left, label);
  const rhs = indexById(right, label);
  const added: IdName[] = [];
  const removed: IdName[] = [];
  const modified: IdName[] = [];
  for (const [id, r] of rhs) {
    const l = lhs.get(id);
    if (!l) added.push({ id, label: r.label });
    else if (changed(l.item, r.item)) modified.push({ id, label: r.label });
  }
  for (const [id, l] of lhs) {
    if (!rhs.has(id)) removed.push({ id, label: l.label });
  }
  return { added, removed, modified };
}

export function CheckpointDiffModal({
  base,
  head,
  baseLabel,
  headLabel,
  onClose,
}: Props) {
  const diff = useMemo(() => {
    const baseSpec = base.spec as DesignSpec | undefined;
    const headSpec = head.spec as DesignSpec | undefined;
    if (!baseSpec || !headSpec) return null;

    const partsDiff = diffByKey(
      baseSpec.parts,
      headSpec.parts,
      (p) => `${p.kind} · ${p.name || p.id}`,
      (a, b) => JSON.stringify(a) !== JSON.stringify(b),
    );
    const jointsDiff = diffByKey(
      baseSpec.joints,
      headSpec.joints,
      (j) => `${j.type} · ${j.fromPartId} ↔ ${j.toPartId}`,
      (a, b) => JSON.stringify(a) !== JSON.stringify(b),
    );
    const featuresDiff = diffByKey(
      baseSpec.features,
      headSpec.features,
      (f) => `${f.kind} · ${f.partId}${f.name ? ` (${f.name})` : ''}`,
      (a, b) => JSON.stringify(a) !== JSON.stringify(b),
    );
    const paramsDiff = diffByKey(
      baseSpec.parameters?.map((p, i) => ({ id: p.name, ...p, idx: i })) as ({ id: string } & typeof baseSpec.parameters[number])[] | undefined,
      headSpec.parameters?.map((p, i) => ({ id: p.name, ...p, idx: i })) as ({ id: string } & typeof headSpec.parameters[number])[] | undefined,
      (p) => `${p.displayName ?? p.name} = ${p.value}`,
      (a, b) => a.value !== b.value,
    );
    return {
      partsDiff,
      jointsDiff,
      featuresDiff,
      paramsDiff,
      qualityChanged: baseSpec.qualityLevel !== headSpec.qualityLevel,
      baseQuality: baseSpec.qualityLevel,
      headQuality: headSpec.qualityLevel,
      baseCounts: {
        parts: baseSpec.parts?.length ?? 0,
        joints: baseSpec.joints?.length ?? 0,
        features: baseSpec.features?.length ?? 0,
      },
      headCounts: {
        parts: headSpec.parts?.length ?? 0,
        joints: headSpec.joints?.length ?? 0,
        features: headSpec.features?.length ?? 0,
      },
    };
  }, [base, head]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-brand-indigo/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-synergy-neutral-700 bg-synergy-bg-secondary-dark text-synergy-text-primary shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-synergy-neutral-700 px-4 py-3">
          <div className="flex items-center gap-2">
            <GitCompare className="h-4 w-4 text-brand-indigo" />
            <span className="text-sm font-semibold">Checkpoint diff</span>
            <span className="font-mono text-xs text-synergy-text-tertiary">
              {baseLabel} → {headLabel}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-7 w-7 rounded-md p-0 text-synergy-text-tertiary hover:bg-synergy-neutral-800 hover:text-synergy-text-primary"
          >
            <X className="h-4 w-4" />
          </Button>
        </header>

        <ScrollArea className="flex-1">
          <div className="flex flex-col gap-3 p-4 text-xs">
            {!diff && (
              <div className="text-synergy-neutral-400">
                One of the checkpoints is missing its spec snapshot — cannot diff.
              </div>
            )}
            {diff && (
              <>
                <CountsHeader
                  baseLabel={baseLabel}
                  headLabel={headLabel}
                  baseCounts={diff.baseCounts}
                  headCounts={diff.headCounts}
                />
                {diff.qualityChanged && (
                  <div className="rounded-md border-2 border-brand-indigo/30 bg-brand-teal-soft/40 px-2.5 py-1.5">
                    <span className="font-semibold text-brand-indigo">
                      Quality level: <span className="font-mono">{diff.baseQuality ?? 'unset'}</span>{' '}
                      → <span className="font-mono">{diff.headQuality ?? 'unset'}</span>
                    </span>
                  </div>
                )}
                <DiffSection title="Parts" diff={diff.partsDiff} />
                <DiffSection title="Joints" diff={diff.jointsDiff} />
                <DiffSection title="Features" diff={diff.featuresDiff} />
                <DiffSection title="Parameters" diff={diff.paramsDiff} />
                <CodeHashCompare base={base} head={head} />
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

function CountsHeader({
  baseLabel,
  headLabel,
  baseCounts,
  headCounts,
}: {
  baseLabel: string;
  headLabel: string;
  baseCounts: { parts: number; joints: number; features: number };
  headCounts: { parts: number; joints: number; features: number };
}) {
  const row = (label: string, b: number, h: number) => (
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-synergy-text-tertiary">{label}</span>
      <div className="flex items-center gap-2 font-mono">
        <span>{b}</span>
        <span className="text-synergy-text-tertiary">→</span>
        <span
          className={
            h > b
              ? 'text-brand-indigo font-bold'
              : h < b
                ? 'text-brand-indigo/60 font-bold'
                : 'text-brand-indigo'
          }
        >
          {h}
        </span>
      </div>
    </div>
  );
  return (
    <div className="rounded-md border border-synergy-neutral-700/60 bg-synergy-neutral-900/40 p-2.5">
      <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-synergy-text-tertiary">
        <span>{baseLabel}</span>
        <span>{headLabel}</span>
      </div>
      {row('Parts', baseCounts.parts, headCounts.parts)}
      {row('Joints', baseCounts.joints, headCounts.joints)}
      {row('Features', baseCounts.features, headCounts.features)}
    </div>
  );
}

function DiffSection({
  title,
  diff,
}: {
  title: string;
  diff: { added: IdName[]; removed: IdName[]; modified: IdName[] };
}) {
  const total = diff.added.length + diff.removed.length + diff.modified.length;
  if (total === 0) {
    return (
      <div className="flex items-center justify-between rounded-md border border-synergy-neutral-700/40 bg-synergy-neutral-900/20 px-2.5 py-1.5 text-synergy-text-tertiary">
        <span className="font-medium">{title}</span>
        <span className="text-[10px]">no changes</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-synergy-neutral-700/60 bg-synergy-neutral-900/40 p-2.5">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold">
        <span>{title}</span>
        <span className="text-synergy-text-tertiary">({total})</span>
      </div>
      {diff.added.map((it) => (
        <Row key={`+${it.id}`} icon={Plus} color="text-brand-indigo" id={it.id} label={it.label} />
      ))}
      {diff.removed.map((it) => (
        <Row key={`-${it.id}`} icon={Minus} color="text-brand-indigo/60" id={it.id} label={it.label} />
      ))}
      {diff.modified.map((it) => (
        <Row key={`~${it.id}`} icon={Pencil} color="text-brand-indigo" id={it.id} label={it.label} />
      ))}
    </div>
  );
}

function Row({
  icon: Icon,
  color,
  id,
  label,
}: {
  icon: typeof Plus;
  color: string;
  id: string;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      <Icon className={`h-3 w-3 shrink-0 ${color}`} />
      <code className="rounded bg-synergy-neutral-800/60 px-1 py-0.5 font-mono text-[10px]">{id}</code>
      <span className="truncate text-synergy-text-secondary">{label}</span>
    </div>
  );
}

function CodeHashCompare({ base, head }: { base: Checkpoint; head: Checkpoint }) {
  const baseHash = base.specHash?.slice(0, 16) ?? '(none)';
  const headHash = head.specHash?.slice(0, 16) ?? '(none)';
  return (
    <div className="rounded-md border border-synergy-neutral-700/40 bg-synergy-neutral-900/20 p-2.5 text-[10px] text-synergy-text-tertiary">
      <div className="flex items-center justify-between">
        <span>Spec fingerprint</span>
        <div className="flex items-center gap-1.5 font-mono">
          <span>{baseHash}</span>
          <span>→</span>
          <span className={baseHash !== headHash ? 'text-brand-indigo font-bold' : ''}>{headHash}</span>
        </div>
      </div>
    </div>
  );
}
